import { get } from 'lodash';

/**
 * HTTP Status Code to User-Friendly Message Mapping
 * Only includes codes actually thrown by the ar-collections backend
 */
const STATUS_CODE_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'You are not authorized. Please log in and try again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  500: 'An unexpected server error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
};

// Server responses pass through `GlobalExceptionFilter`, which wraps the original
// HttpException body as `{ statusCode, timestamp, path, message }`. For class-validator
// BadRequests, `message` is itself `{ message: string[], error, statusCode }`. The shared
// fetch wrapper then JSON.stringifies that envelope into `Error.message`. Unwrap both
// layers and surface the first validator line.
const tryParseJson = (s: string): unknown => {
  const trimmed = s.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const normalizeMessage = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const parsed = tryParseJson(value);
    if (parsed != null) return normalizeMessage(parsed);
    return value;
  }
  if (Array.isArray(value)) {
    const first = value.find(v => typeof v === 'string');
    return typeof first === 'string' ? first : null;
  }
  if (typeof value === 'object') {
    const inner = (value as { message?: unknown }).message;
    if (inner !== undefined) return normalizeMessage(inner);
  }
  return null;
};

/**
 * Extracts a user-friendly error message from an API error response.
 *
 * Priority:
 * 1. `response.data.message` - Backend-provided message (axios shape)
 * 2. `error.message` - Thrown Error from the shared fetch wrapper (unwrapped + first validator line)
 * 3. Status code mapping - User-friendly message based on HTTP status
 * 4. Default message - Fallback when no other info is available
 *
 * @param error - The error object from axios/API call
 * @param defaultMessage - Fallback message if no other message is found
 * @returns User-friendly error message string
 */
export const getApiErrorMessage = (
  error: any,
  defaultMessage = 'An unexpected error occurred. Please try again.'
): string => {
  const backendMessage = normalizeMessage(get(error, 'response.data.message'));
  if (backendMessage) return backendMessage;

  const thrownMessage = normalizeMessage(get(error, 'message'));
  if (thrownMessage) return thrownMessage;

  const statusCode = get(error, 'response.status');
  if (statusCode && STATUS_CODE_MESSAGES[statusCode]) {
    return STATUS_CODE_MESSAGES[statusCode];
  }

  return defaultMessage;
};
